// Translate pypic's TOML PlotTheme schema into the {ui, scene, axes, cycle,
// colormaps} shape that magviz consumers (control-pane CSS injection,
// applyThemeToScene, src/scene/axes.ts) already understand. Pure functions —
// no I/O, no globals — so this module is trivial to unit-test.
//
// pypic colors come in two flavors:
//   - hex strings: "#rrggbb"
//   - RGBA arrays: [r, g, b, a] with all components in 0..1
// magviz consumers expect CSS color strings (for UI palette) and hex strings
// (for THREE.Color in the scene). The helpers below cover both.

import { MAGVIZ_BRAND, rgbaBrand } from './themeCss';

type ColorValue = string | number[] | null | undefined;

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));
const to255 = (n: number): number => Math.round(clamp01(n) * 255);

export function rgbaArrayToCss(arr: number[] | null | undefined): string | null {
    if (!Array.isArray(arr) || arr.length < 3) return null;
    const r = to255(arr[0] ?? 0);
    const g = to255(arr[1] ?? 0);
    const b = to255(arr[2] ?? 0);
    const a = arr.length >= 4 ? clamp01(arr[3] ?? 1) : 1;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function hexOrRgba(value: ColorValue): string | null {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return rgbaArrayToCss(value);
    return null;
}

export function dropAlphaToHex(value: ColorValue): string | null {
    if (typeof value === 'string') return value;
    if (!Array.isArray(value) || value.length < 3) return null;
    const r = to255(value[0] ?? 0).toString(16).padStart(2, '0');
    const g = to255(value[1] ?? 0).toString(16).padStart(2, '0');
    const b = to255(value[2] ?? 0).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
}

function alphaOf(value: ColorValue, fallback = 1): number {
    if (Array.isArray(value) && value.length >= 4) return clamp01(value[3] ?? fallback);
    return fallback;
}

interface PypicTheme {
    name?: string;
    colors?: Record<string, ColorValue | { values?: string[] }>;
    axes?: { x_color?: string; y_color?: string; z_color?: string; arrows?: boolean };
    colormaps?: { sequential?: string; diverging?: string };
    font?: { family?: string[] | string; title?: number; label?: number; tick?: number; overlay?: number };
    slider?: { fill?: ColorValue; grip?: ColorValue; tick?: ColorValue; radius?: number; height?: number; grip_size?: number };
}

// TOML px knobs arrive as numbers; emit `<n>px`, falling back when absent/garbage.
const pxOr = (v: unknown, d: number): string => `${typeof v === 'number' && Number.isFinite(v) ? v : d}px`;

// Map a parsed pypic TOML object to magviz's theme shape. Unknown sections
// are ignored. Missing sections fall back to safe defaults.
export function pypicToMagviz(pypic: Record<string, unknown>): Record<string, unknown> {
    const p = pypic as unknown as PypicTheme;
    const colors = (p && p.colors) || {} as PypicTheme['colors'];
    const c = colors as Record<string, ColorValue | { values?: string[] }>;
    const cycleEntry = c['cycle'] as { values?: string[] } | undefined;
    const cycleArr = (cycleEntry && Array.isArray(cycleEntry.values)) ? cycleEntry.values.slice() : [];
    const axes = (p && p.axes) || {};
    const colormaps = (p && p.colormaps) || {};
    const sl = ((p && p.slider) || {}) as NonNullable<PypicTheme['slider']>;

    const bg = typeof c['background'] === 'string' ? c['background'] as string : '#000000';
    const accent = typeof c['accent'] === 'string' ? c['accent'] as string : null;

    const gridAlpha = alphaOf(c['grid'] as ColorValue, 0.15);
    const grid = {
        color: dropAlphaToHex(c['grid'] as ColorValue) || '#ffffff',
        minorOpacity: gridAlpha,
        majorOpacity: Math.min(1, gridAlpha * 2.5),
    };

    const ui: Record<string, string> = {
        bodyBg: bg,
        textColor: hexOrRgba(c['text'] as ColorValue) || '#ffffff',
        tweakTextColor: hexOrRgba(c['secondary_text'] as ColorValue) || hexOrRgba(c['text'] as ColorValue) || '#ffffff',
        panelBg: hexOrRgba(c['overlay'] as ColorValue) || 'rgba(15, 20, 35, 0.38)',
        perfBg: hexOrRgba(c['overlay'] as ColorValue) || 'rgba(15, 20, 35, 0.5)',
        helpBg: hexOrRgba(c['overlay'] as ColorValue) || 'rgba(15, 20, 35, 0.6)',
        panelHeaderBg: hexOrRgba(c['overlay_alt'] as ColorValue) || hexOrRgba(c['overlay'] as ColorValue) || 'rgba(30, 40, 70, 0.3)',
        inputBg: hexOrRgba(c['overlay_alt'] as ColorValue) || hexOrRgba(c['overlay'] as ColorValue) || 'rgba(24, 30, 50, 0.9)',
        buttonBg: hexOrRgba(c['overlay_alt'] as ColorValue) || hexOrRgba(c['overlay'] as ColorValue) || '#223456',
        buttonHoverBg: hexOrRgba(c['overlay_alt'] as ColorValue) || hexOrRgba(c['overlay'] as ColorValue) || '#2b3f6b',
        buttonText: hexOrRgba(c['overlay_alt_text'] as ColorValue) || hexOrRgba(c['text'] as ColorValue) || '#ffffff',
        panelBorder:           hexOrRgba(c['overlay_border'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.22),
        panelHeaderBorder:     hexOrRgba(c['overlay_border'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.30),
        paneBorder:              hexOrRgba(c['overlay_border'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.18),
        helpBorder:            hexOrRgba(c['overlay_border'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.30),
        sliderTrack:           hexOrRgba(c['track'] as ColorValue)          || rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.32),
        scrollbarTrack:        hexOrRgba(c['track'] as ColorValue)          || 'rgba(30, 40, 70, 0.10)',
        scrollbarThumb:        accent || hexOrRgba(c['track_alt'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.32),
        scrollbarThumbBorder:  hexOrRgba(c['overlay_border'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.22),
        scrollbarThumbHover:   accent || rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.42),
        accent:                accent || MAGVIZ_BRAND.accent,
        thumbBorder:           '#ffffff',
        sliderFill:            hexOrRgba(sl.fill) || accent || MAGVIZ_BRAND.accent,
        sliderGrip:            hexOrRgba(sl.grip) || hexOrRgba(sl.fill) || accent || MAGVIZ_BRAND.accent,
        sliderTick:            hexOrRgba(sl.tick) || hexOrRgba(c['track'] as ColorValue) || rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.25),
        sliderRadius:          pxOr(sl.radius, 7),
        sliderHeight:          pxOr(sl.height, 3),
        sliderGripSize:        pxOr(sl.grip_size, 21),
    };

    const scene = {
        background: bg,
        grid,
        gridLabelColor: hexOrRgba(c['secondary_text'] as ColorValue) || hexOrRgba(c['text'] as ColorValue) || '#9aa7c0',
        vectorLabelColor: hexOrRgba(c['text'] as ColorValue) || null,
    };

    const themeAxes = {
        x: typeof axes.x_color === 'string' ? axes.x_color : null,
        y: typeof axes.y_color === 'string' ? axes.y_color : null,
        z: typeof axes.z_color === 'string' ? axes.z_color : null,
        arrows: typeof axes.arrows === 'boolean' ? axes.arrows : true,
    };

    const fontTable = (p && p.font) || {};
    const familyRaw = fontTable.family;
    const family = Array.isArray(familyRaw)
        ? familyRaw.filter((s) => typeof s === 'string')
        : (typeof familyRaw === 'string' ? [familyRaw] : ['Segoe UI', 'Arial', 'sans-serif']);
    const font = {
        family,
        sizeTitle:   Number(fontTable.title)   || 16,
        sizeLabel:   Number(fontTable.label)   || 13,
        sizeTick:    Number(fontTable.tick)    || 12,
        sizeOverlay: Number(fontTable.overlay) || 13,
    };

    return {
        name: typeof p.name === 'string' ? p.name : null,
        ui,
        font,
        scene,
        axes: themeAxes,
        cycle: cycleArr,
        colormaps: {
            sequential: typeof colormaps.sequential === 'string' ? colormaps.sequential : null,
            diverging: typeof colormaps.diverging === 'string' ? colormaps.diverging : null,
        },
    };
}

interface SynthColormapStop { pos: number; r: number; g: number; b: number }

// Build a magviz-shaped colormap def (matching data/colormaps/*.json) from
// a pypic cycle of hex colors. The cycle is qualitative (8 distinct colors),
// so we lay them out evenly across 0..1 and let the runtime interpolate.
export function synthCycleColormap(cycleHexArray: string[] | null | undefined, name = 'Theme Cycle'): { name: string; colors: SynthColormapStop[] } | null {
    if (!Array.isArray(cycleHexArray) || cycleHexArray.length === 0) return null;
    const n = cycleHexArray.length;
    const colors = cycleHexArray.map((hex, i) => {
        const { r, g, b } = parseHex(hex);
        const pos = n === 1 ? 0 : i / (n - 1);
        return { pos: Number(pos.toFixed(4)), r, g, b };
    });
    return { name, colors };
}

function parseHex(hex: string): { r: number; g: number; b: number } {
    if (typeof hex !== 'string') return { r: 0, g: 0, b: 0 };
    let s = hex.trim().replace(/^#/, '');
    if (s.length === 3) s = s.split('').map((char) => char + char).join('');
    const n = parseInt(s, 16);
    if (Number.isNaN(n)) return { r: 0, g: 0, b: 0 };
    return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}
